/**
 * Mapping Shopify -> modèle produit du storefront.
 *
 * Shopify est la source de vérité commerce : titre, description, prix, images,
 * variantes (couleurs / tailles), disponibilité, panier et commandes.
 * Le catalogue local (`@/lib/catalog`) ne sert plus que de « surcouche
 * présentation » (nuanciers, images optimisées AVIF/WebP, rattachement collection)
 * et de repli tant que les données Shopify ne sont pas chargées.
 */
import { queryOptions } from "@tanstack/react-query";
import { PRODUCTS_QUERY, storefrontApiRequest, type ShopifyProductNode } from "@/lib/shopify";
import { productDetails, type ColorVariant, type Product, type ProductDetail, type SizeVariant } from "@/lib/catalog";

export type ShopVariant = {
  /** Identifiant GraphQL de la variante Shopify (merchandiseId du panier). */
  id: string;
  title: string;
  price: number;
  available: boolean;
  color?: string;
  size?: string;
};

export type ShopProduct = ProductDetail & {
  handle: string;
  shopifyId: string;
  variants: ShopVariant[];
  available: boolean;
};

const COLOR_RE = /couleur|color/i;
const SIZE_RE = /taille|size/i;
const UNIQUE_RE = /unique|default title|one size/i;

const FALLBACK_SWATCH: Record<string, string> = {
  noir: "#1C1B1A",
  blanc: "#F6F1E7",
  rose: "#E9A7B0",
  bleu: "#5B7FA6",
  orange: "#F26B21",
  vert: "#3F6B57",
  beige: "#E4D8C5",
  doré: "#C9A227",
};

const TYPE_ROUTES: Array<[RegExp, string]> = [
  [/maillot/i, "/maillots"],
  [/kimono/i, "/kimonos"],
  [/sac|cabas|panier/i, "/sacs"],
  [/bijou|accessoire|foulard/i, "/accessoires"],
  [/beachwear|plage/i, "/beachwear"],
];

function slugify(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function routeFor(productType: string, tags: string[]) {
  const haystack = `${productType} ${tags.join(" ")}`;
  const match = TYPE_ROUTES.find(([re]) => re.test(haystack));
  return match?.[1] ?? "/nouveautes";
}

/** Extrait le premier paragraphe et les puces d'une description HTML Shopify. */
function parseDescription(html: string, fallback: string) {
  const strip = (s: string) =>
    s
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&#39;|&rsquo;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/\s+/g, " ")
      .trim();

  const paragraph = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  const bullets = [...html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)].map((m) => strip(m[1]!)).filter(Boolean);

  return {
    shortDescription: paragraph ? strip(paragraph[1]!) : strip(html) || fallback,
    details: bullets,
  };
}

/** Identifiant numérique Shopify extrait d'un GID (`gid://shopify/Product/123` -> `123`). */
export function shopifyNumericId(gid: string): string {
  return gid.split("/").pop() ?? gid;
}

/** Slug storefront d'un produit Shopify : tag `slug:` en priorité, sinon handle. */
export function slugForNode(node: Pick<ShopifyProductNode, "tags" | "handle">): string {
  const slugTag = node.tags.find((t) => t.toLowerCase().startsWith("slug:"));
  return slugTag ? slugTag.slice(5).trim() : node.handle;
}

/**
 * Résolution du mapping Lovable -> Shopify.
 *
 * 1. Si la fiche locale déclare `shopifyProductId`, SEUL ce produit Shopify est
 *    retenu : un doublon portant le même tag `slug:` ne peut plus prendre sa place.
 * 2. Sans identifiant épinglé, le repli historique par tag `slug:` / handle reste
 *    actif ; en cas de doublon, on remonte une erreur explicite au lieu de choisir
 *    silencieusement le premier résultat.
 */
export function resolveCatalogNodes(nodes: ShopifyProductNode[]): ShopifyProductNode[] {
  const pinned = new Map<string, string>();
  for (const [slug, detail] of Object.entries(productDetails)) {
    if (detail.shopifyProductId) pinned.set(slug, String(detail.shopifyProductId));
  }

  const bySlug = new Map<string, ShopifyProductNode[]>();
  for (const node of nodes) {
    const slug = slugForNode(node);
    const group = bySlug.get(slug);
    if (group) group.push(node);
    else bySlug.set(slug, [node]);
  }

  const resolved: ShopifyProductNode[] = [];
  for (const [slug, group] of bySlug) {
    const pin = pinned.get(slug);

    if (pin) {
      const match = group.find((n) => shopifyNumericId(n.id) === pin);
      if (!match) {
        console.error(
          `[catalogue] Produit Shopify ${pin} introuvable pour « ${slug} ». ` +
            `Aucun repli automatique : vérifiez shopifyProductId dans src/lib/catalog.ts.`,
        );
        continue;
      }
      if (group.length > 1) {
        console.warn(
          `[catalogue] ${group.length} produits Shopify portent le slug « ${slug} ». ` +
            `Seul ${pin} est utilisé ; doublons ignorés : ` +
            group.filter((n) => n !== match).map((n) => shopifyNumericId(n.id)).join(", "),
        );
      }
      resolved.push(match);
      continue;
    }

    if (group.length > 1) {
      console.error(
        `[catalogue] Doublon Shopify détecté pour « ${slug} » : ` +
          group.map((n) => shopifyNumericId(n.id)).join(", ") +
          `. Ajoutez shopifyProductId dans src/lib/catalog.ts pour lever l'ambiguïté ` +
          `(aucune sélection silencieuse du premier résultat).`,
      );
    }
    const first = group[0];
    if (first) resolved.push(group.find((n) => n.availableForSale) ?? first);
  }

  return resolved;
}

/** Convertit un produit Shopify en produit storefront (design inchangé). */
export function mapShopifyProduct(node: ShopifyProductNode): ShopProduct {
  const slug = slugForNode(node);
  const local = productDetails[slug];


  const shopifyImages = node.images.edges.map((e) => e.node.url);
  const variants: ShopVariant[] = node.variants.edges.map(({ node: v }) => ({
    id: v.id,
    title: v.title,
    price: Number(v.price.amount),
    available: v.availableForSale,
    ...(v.selectedOptions.find((o) => COLOR_RE.test(o.name))?.value
      ? { color: v.selectedOptions.find((o) => COLOR_RE.test(o.name))!.value }
      : {}),
    ...(v.selectedOptions.find((o) => SIZE_RE.test(o.name))?.value
      ? { size: v.selectedOptions.find((o) => SIZE_RE.test(o.name))!.value }
      : {}),
  }));

  const colorOption = node.options.find((o) => COLOR_RE.test(o.name));
  const sizeOption = node.options.find((o) => SIZE_RE.test(o.name));
  const realSizes = (sizeOption?.values ?? []).filter((v) => !UNIQUE_RE.test(v));
  const uniqueSizeLabel = sizeOption && realSizes.length === 0 ? sizeOption.values[0] : undefined;

  const sizesFrom = (color?: string): SizeVariant[] =>
    realSizes.map((size) => {
      const v = variants.find((x) => x.size === size && (!color || x.color === color));
      return { size, sku: v?.id ?? `${slug}-${size}`, stock: v && !v.available ? 0 : null };
    });

  const colors: ColorVariant[] | undefined = colorOption
    ? colorOption.values.map((value) => {
        const localColor = local?.colors?.find(
          (c) => c.label.toLowerCase() === value.toLowerCase() || c.id === slugify(value),
        );
        const variantImage = node.variants.edges.find(
          ({ node: v }) => v.selectedOptions.some((o) => COLOR_RE.test(o.name) && o.value === value) && v.image,
        )?.node.image?.url;
        return {
          id: localColor?.id ?? slugify(value),
          label: value,
          swatch: localColor?.swatch ?? FALLBACK_SWATCH[value.toLowerCase()] ?? "#E4D8C5",
          // Visuels locaux optimisés quand ils existent, sinon images Shopify.
          images:
            localColor?.images ??
            (variantImage ? [variantImage, ...shopifyImages.filter((u) => u !== variantImage)] : shopifyImages),
          sizes: sizesFrom(value),
        };
      })
    : undefined;

  const parsed = parseDescription(node.descriptionHtml || "", node.description);
  const images = local?.images ?? shopifyImages;
  const price = Number(node.priceRange.minVariantPrice.amount);

  const product: ShopProduct = {
    id: slug,
    slug,
    handle: node.handle,
    shopifyId: node.id,
    name: node.title,
    price,
    category: local?.category ?? node.productType ?? "Boutique",
    subcategory: local?.subcategory ?? node.productType ?? "",
    isNew: node.tags.some((t) => /nouveaut|new/i.test(t)),
    image: local?.image ?? shopifyImages[0] ?? "",
    hoverImage: local?.hoverImage ?? shopifyImages[1] ?? "",
    shortDescription: parsed.shortDescription || local?.shortDescription || "",
    details: parsed.details.length > 0 ? parsed.details : (local?.details ?? []),
    categoryTo: local?.categoryTo ?? routeFor(node.productType, node.tags),
    available: node.availableForSale,
    variants,
    ...(colors ? { colors } : {}),
    ...(colors ? {} : { images }),
    ...(colors || realSizes.length === 0 ? {} : { sizes: sizesFrom() }),
    ...(uniqueSizeLabel ? { sizeLabel: uniqueSizeLabel } : local?.sizeLabel ? { sizeLabel: local.sizeLabel } : {}),
    ...(!colorOption && local?.colorLabel ? { colorLabel: local.colorLabel } : {}),
    ...(local?.ogImage ? { ogImage: local.ogImage } : {}),
  };

  return product;
}

/** Registre runtime : permet au panier de retrouver les variantes Shopify. */
const registry = new Map<string, ShopProduct>();

export function registerProducts(products: ShopProduct[]) {
  for (const p of products) registry.set(p.slug, p);
}

export function getRegisteredProduct(slug: string): ShopProduct | undefined {
  return registry.get(slug);
}

/** Retrouve l'identifiant de variante Shopify pour un couple couleur / taille. */
export function findVariantId(
  product: Pick<ShopProduct, "variants"> | undefined,
  opts: { colorLabel?: string | undefined; size?: string | undefined },
): string | undefined {
  if (!product?.variants?.length) return undefined;
  const match = product.variants.find(
    (v) =>
      (!opts.colorLabel || (v.color ?? "").toLowerCase() === opts.colorLabel.toLowerCase()) &&
      (!opts.size || (v.size ?? "") === opts.size),
  );
  return (match ?? product.variants[0])?.id;
}

export async function fetchShopifyProducts(): Promise<ShopProduct[]> {
  const data = await storefrontApiRequest<{
    data?: { products?: { edges: Array<{ node: ShopifyProductNode }> } };
  }>(PRODUCTS_QUERY, { first: 250 });

  const edges = data?.data?.products?.edges ?? [];
  const products = resolveCatalogNodes(edges.map(({ node }) => node)).map(mapShopifyProduct);
  registerProducts(products);
  return products;
}

export const shopifyProductsQuery = queryOptions({
  queryKey: ["shopify", "products"],
  queryFn: fetchShopifyProducts,
  staleTime: 5 * 60 * 1000,
  retry: 1,
});

/** Filtre par collection : on s'appuie sur le type de produit, les tags et la route. */
export function filterByCollection(products: ShopProduct[], route: string, extra?: RegExp) {
  return products.filter(
    (p) => p.categoryTo === route || (extra ? extra.test(`${p.category} ${p.subcategory}`) : false),
  );
}

export function toCards(products: ShopProduct[]): Product[] {
  return products;
}
